// @ts-nocheck
import { cancelReasonLabel } from "@kawkaw/shared";
import { refundDisplayId } from "../utils/refund";
import { useState, useMemo } from "react";
import {
  Input, Select, Button, Modal, Form, Dropdown, App, DatePicker, Table,
} from "../components/admin-ui";
import type { MenuProps } from "../components/admin-ui";
import {
  MoreOutlined, DownloadOutlined,
  WarningOutlined, ReloadOutlined, ContainerOutlined, PieChartOutlined, CalendarOutlined, ApiOutlined,
} from "../components/admin-icons";
import type { ColumnsType } from "../components/admin-ui";
import dayjs from "dayjs";
import { Search, X } from "lucide-react";
import { jsPDF } from "jspdf";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusBadge } from "../components/StatusBadge";
import { AdminShell } from "../layouts/AdminShell";
import {
  useAdminRefunds, useReviewRefund, useRefundStats,
  type AdminRefundItem,
} from "../api/hooks";
import { formatOrderDate, formatOrderTime } from "../utils/formatLocaleDate";
import { formatTransactionId } from "../utils/transactionUi";
import { useElementHeight } from "../hooks/useElementHeight";

const { RangePicker } = DatePicker;

const REFUNDS_PAGE_CLASS = [
  "admin-list-page",
  "bg-[#f8f9fa] px-8 pb-6 pt-7",
  "[&_table]:border-separate [&_table]:border-spacing-0 [&_table]:bg-transparent",
  "[&_th]:h-12 [&_th]:border-b-0 [&_th]:bg-[rgba(243,244,245,0.5)] [&_th]:px-6 [&_th]:py-4 [&_th]:text-xs [&_th]:font-extrabold [&_th]:uppercase [&_th]:tracking-[0.6px] [&_th]:text-[#534434]",
  "[&_td]:min-h-[88px] [&_td]:border-b [&_td]:border-[#f8fafc] [&_td]:bg-transparent [&_td]:px-6 [&_td]:py-8 [&_td]:align-middle [&_td]:text-[#191c1d]",
  "[&_tr:hover_td]:bg-transparent [&_tbody_tr]:cursor-default",
  "[&_th:first-child]:pl-6 [&_td:first-child]:pl-6 [&_th:last-child]:pr-12 [&_td:last-child]:pr-12 [&_th:last-child]:text-left [&_td:last-child]:text-left",
].join(" ");

const REFUNDS_FILTER_CLASS = "fixed right-0 top-16 z-40 bg-[#f8f9fa] px-8 pb-4 pt-8 left-64 max-[900px]:left-[76px] max-[900px]:px-4 min-[901px]:max-[1280px]:left-[220px] min-[901px]:max-[1280px]:px-5";

// ─── Constants ───────────────────────────────────────────────────────────────

const PAYMENT_I18N_KEYS: Record<string, string> = {
  visa: "refunds.payVisa", fpx: "refunds.payFpx", grabpay: "refunds.payGrab",
  cash: "refunds.payCash", ewallet: "refunds.payEwallet", tng: "refunds.payTng", boost: "refunds.payBoost",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────


function rowClassName(_: AdminRefundItem, index: number) {
  return index % 2 === 0
    ? "[&>td]:!bg-white hover:[&>td]:!bg-white"
    : "[&>td]:!bg-transparent hover:[&>td]:!bg-transparent";
}

function escapeXml(value: unknown) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function wrapPdfText(value: unknown, maxChars: number) {
  const text = String(value ?? "-");
  const lines: string[] = [];
  let line = "";
  for (const char of text) {
    if (line.length >= maxChars) {
      lines.push(line);
      line = "";
    }
    line += char;
  }
  if (line) lines.push(line);
  return lines.length ? lines : ["-"];
}

function svgText(lines: string[], x: number, y: number, options: { size?: number; weight?: number; color?: string; lineHeight?: number } = {}) {
  const size = options.size ?? 18;
  const weight = options.weight ?? 500;
  const color = options.color ?? "#191c1d";
  const lineHeight = options.lineHeight ?? size + 7;
  return lines
    .map((line, index) => `<text x="${x}" y="${y + index * lineHeight}" font-size="${size}" font-weight="${weight}" fill="${color}">${escapeXml(line)}</text>`)
    .join("");
}

function renderRefundPdfPageToPng(svg: string, width: number, height: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("Canvas is not available"));
        return;
      }
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(image, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unable to render refund PDF page"));
    };
    image.src = url;
  });
}

function StatusPill({ status }: { status: string }) {
  return <StatusBadge status={status} kind="refund" className="admin-data-badge" />;
}

function TypeBadge({ type }: { type: string }) {
  return <StatusBadge status={type} kind="refundType" size="xs" className="admin-data-badge" />;
}

// ─── Update Status Modal ─────────────────────────────────────────────────────

function UpdateStatusModal({
  refund,
  onClose,
}: {
  refund: AdminRefundItem;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const reviewRefund = useReviewRefund();
  const [selectedStatus, setSelectedStatus] = useState<string>("completed");

  const onSubmit = async (vals: {
    status: string;
    transfer_method?: string;
    reference_note?: string;
  }) => {
    const action = vals.status === "rejected" ? "reject" : "approve";
    // Auto-gateway approve needs none of the manual transfer fields — backend
    // handles it entirely via provider.refund(), see admin/routes.ts PATCH.
    const isAutoApprove = action === "approve" && refund.autoRefundSupported;
    try {
      const result = await reviewRefund.mutateAsync({
        id: refund.id,
        action,
        note: isAutoApprove ? undefined : vals.reference_note,
        transfer_method: isAutoApprove ? undefined : vals.transfer_method,
        // Auto-stamp the transfer time at submission — no manual input needed.
        transfer_time: action === "approve" && !isAutoApprove ? new Date().toISOString() : undefined,
      });
      if (isAutoApprove) {
        // Gateway refund result isn't always a clean success — surface the
        // real outcome instead of assuming "completed" always means paid back.
        const finalStatus = result?.data?.status;
        if (finalStatus === "failed") {
          const reason = result.data.rejection_note ? `: ${result.data.rejection_note}` : "";
          void message.error(t("refunds.autoRefundFailedToast", { reason }));
        } else if (finalStatus === "pending") {
          void message.info(t("refunds.autoRefundPendingToast"));
        } else {
          void message.success(t("refunds.autoRefundSuccessToast"));
        }
      } else {
        void message.success(t("refunds.statusUpdated"));
      }
      onClose();
    } catch {
      void message.error(t("refunds.statusUpdateFailed"));
    }
  };

  const rfdId = refundDisplayId(refund.id);
  const txnRef = formatTransactionId(refund.transaction_id);
  const orderCode = refund.order?.order_code ?? "—";
  const customerName = refund.order?.customer?.name ?? t("common.guest");
  const currentStatusLabel = t(`refunds.pill${refund.status.charAt(0).toUpperCase()}${refund.status.slice(1)}`, { defaultValue: refund.status.toUpperCase() });
  const typeLabel = refund.type === "full" ? t("refunds.typeFullRef") : refund.type === "partial" ? t("refunds.typePartialRef") : refund.type.toUpperCase();

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={760}
      centered
      destroyOnHidden
      title={
        <div className="px-0.5 py-1">
          <div className="text-xl font-black leading-tight text-[#191c1d]">{t("refunds.modalTitle")}</div>
          <div className="mt-1.5 text-xs font-extrabold uppercase tracking-[0.5px] text-[#867461]">
            {t("refunds.modalRefundId")} {rfdId}
          </div>
        </div>
      }
    >
      <div className="mb-5 grid grid-cols-[minmax(0,1.15fr)_minmax(240px,0.85fr)] gap-3.5 pt-[18px] max-[700px]:grid-cols-1">
        <div className="rounded-[18px] border border-[#eee8e2] bg-[#fbfaf8] px-5 py-[18px]">
          <div className="mb-4 flex items-center gap-2.5">
            <div className="flex size-[34px] items-center justify-center rounded-xl bg-[#fff7ed]">
              <WarningOutlined className="text-[15px] text-[#ea580c]" />
            </div>
            <span className="text-xs font-black uppercase tracking-[0.8px] text-[#534434]">
              {t("refunds.summaryTitle")}
            </span>
          </div>
          <div className="grid gap-[11px]">
            {[
              { label: t("refunds.customer"), value: customerName },
              { label: t("refunds.orderId"), value: `#${orderCode}` },
              { label: t("refunds.transactionId"), value: txnRef },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between gap-3 border-t border-[#eee8e2] pt-2.5">
                <span className="text-xs font-extrabold uppercase text-[#867461]">{label}</span>
                <span className={`text-right text-[13px] font-black text-[#191c1d] ${label === t("refunds.transactionId") ? "font-mono" : ""}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[18px] border border-[#e8ecef] bg-white px-5 py-[18px] shadow-[0_14px_30px_rgba(15,23,42,0.04)]">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.8px] text-[#867461]">
            {t("refunds.refundAmount")}
          </div>
          <div className="mb-3.5 text-[34px] font-black leading-none text-[#191c1d]">
            RM {Number(refund.amount).toFixed(2)}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={refund.type} kind="refundType" label={typeLabel} size="xs" />
            <span className="text-xs font-extrabold text-[#867461]">{t("refunds.currentStatus")}</span>
            <StatusBadge status={refund.status} kind="refund" label={currentStatusLabel} />
          </div>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={vals => void onSubmit(vals as Parameters<typeof onSubmit>[0])}
        initialValues={{ status: "completed" }}>
        <div className="mb-4 rounded-[18px] border border-[#e8ecef] bg-white px-5 py-[18px]">
        <Form.Item name="status" label={<span className="font-black text-[#534434]">{t("refunds.updateStatus")}</span>}>
          <Select
            size="large"
            className="rounded-xl"
            onChange={(v) => setSelectedStatus(v as string)}
            options={[
              { value: "completed", label: t("refunds.optCompleted") },
              { value: "rejected",  label: t("refunds.optRejected") },
            ]}
          />
        </Form.Item>

        {selectedStatus === "completed" && (
          refund.autoRefundSupported ? (
            <div className="mb-1 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] px-[18px] py-4">
              <div className="mb-2.5 flex items-center gap-2.5">
                <div className="flex size-8 items-center justify-center rounded-xl bg-[#ecfdf5]">
                  <ApiOutlined className="text-[15px] text-[#047857]" />
                </div>
                <span className="text-xs font-black uppercase tracking-[0.6px] text-[#047857]">
                  {t("refunds.autoRefundTitle")}
                </span>
              </div>
              <p className="m-0 text-[13px] font-semibold leading-[1.5] text-[#334155]">
                {t("refunds.autoRefundDesc")}
              </p>
            </div>
          ) : (
            <div className="mb-1 rounded-2xl border border-[#dbeafe] bg-[#f8fbff] px-[18px] py-4">
              <div className="mb-4 flex items-center gap-2.5">
                <div className="flex size-8 items-center justify-center rounded-xl bg-[#ecfdf5]">
                  <span className="text-[15px] font-black text-[#047857]">RM</span>
                </div>
                <span className="text-xs font-black uppercase tracking-[0.6px] text-[#047857]">
                  {t("refunds.transferDetails")}
                </span>
              </div>
              <div className="mb-3">
                <Form.Item name="transfer_method" label={t("refunds.transferMethod")} className="!mb-0" rules={[{ required: true, message: t("refunds.transferMethodRequired") }]}>
                  <Select
                    options={[
                      { value: "E-wallet", label: t("refunds.transferEwallet") },
                      { value: "Bank Transfer", label: t("refunds.transferBank") },
                      { value: "Cash", label: t("refunds.transferCash") },
                      { value: "FPX", label: t("refunds.transferFpx") },
                      { value: "Card", label: t("refunds.transferCard") },
                    ]}
                    placeholder={t("refunds.placeholderTransferMethod")}
                  />
                </Form.Item>
              </div>
              <Form.Item name="reference_note" label={t("refunds.referenceNote")} className="!mb-0">
                <Input.TextArea rows={2} placeholder={t("refunds.placeholderReference")} />
              </Form.Item>
            </div>
          )
        )}

        {selectedStatus === "rejected" && (
          <div className="mb-1 rounded-2xl border border-[#fecaca] bg-[#fffafa] px-[18px] py-4">
            <Form.Item name="reference_note" label={<span className="font-black text-[#7f1d1d]">{t("refunds.rejectionReason")}</span>} className="!mb-0">
              <Input.TextArea rows={3} placeholder={t("refunds.placeholderRejection")} />
            </Form.Item>
          </div>
        )}
        </div>

        <div className="mb-5 flex items-start gap-2.5 rounded-[14px] border border-[#fed7aa] bg-[#fff7ed] px-4 py-[13px]">
          <WarningOutlined className="mt-px text-[#ea580c]" />
          <span className="text-[13px] font-bold leading-[1.45] text-[#7c2d12]">
            {selectedStatus === "completed" && refund.autoRefundSupported
              ? t("refunds.warnConfirmAutoRefund")
              : t("refunds.warnConfirmTransfer")}
          </span>
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button size="large" onClick={onClose} className="!h-11 !min-w-[110px] !rounded-xl !font-black">
            {t("refunds.cancel").toUpperCase()}
          </Button>
          <Button
            size="large"
            type="primary"
            htmlType="submit"
            loading={reviewRefund.isPending}
            className="!h-11 !min-w-[180px] !rounded-xl !border-[#ea580c] !bg-[#ea580c] !font-black !shadow-[0_10px_24px_rgba(234,88,12,0.2)]"
          >
            {t("refunds.confirmUpdate").toUpperCase()}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

// ─── Main screen ─────────────────────────────────────────────────────────────

export function RefundsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string | undefined>();
  const [type, setType] = useState<string | undefined>();
  const [method, setMethod] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [updateTarget, setUpdateTarget] = useState<AdminRefundItem | null>(null);
  const [filterRef, filterHeight] = useElementHeight<HTMLDivElement>();

  const filterParams = useMemo(() => ({
    page, limit: 10,
    status: status || undefined,
    type: type || undefined,
    method: method || undefined,
    q: search || undefined,
    from: dateRange?.[0] || undefined,
    to: dateRange?.[1] || undefined,
  }), [page, status, type, method, search, dateRange]);

  const { data, isLoading } = useAdminRefunds(filterParams);
  const { data: stats } = useRefundStats();

  const refunds = (data?.data ?? []) as AdminRefundItem[];
  const totalCount = data?.meta.total ?? 0;

  const TH = (key: string) => (
    <span className="text-[11px] font-black uppercase tracking-[0.5px] text-[#7a6f65]">{t(key)}</span>
  );

  const payLabel = (method: string) => t(PAYMENT_I18N_KEYS[method] ?? method);

  async function exportRefundsPdf() {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidthMm = doc.internal.pageSize.getWidth();
    const pageHeightMm = doc.internal.pageSize.getHeight();
    const pageWidth = 1600;
    const pageHeight = 1120;
    const margin = 60;
    const dateLabel = dateRange
      ? `${formatOrderDate(dateRange[0], i18n.language)} - ${formatOrderDate(dateRange[1], i18n.language)}`
      : t("transactions.datePresetAll");
    const tableWidth = pageWidth - margin * 2;
    const colWidths = [135, 190, 210, 125, 135, 135, 170, 370];
    const typeLabelFor = (value: string) => value === "full" ? t("refunds.typeFull") : value === "partial" ? t("refunds.typePartial") : value;
    const statusLabelFor = (value: string) => value === "pending"
      ? t("refunds.statusPending")
      : value === "success"
      ? t("refunds.statusSuccess")
      : value === "rejected"
      ? t("refunds.statusRejectedPill")
      : value === "failed"
      ? t("refunds.statusFailed")
      : value;
    const rows = refunds.map((row) => ({
      refundId: refundDisplayId(row.id),
      txnId: formatTransactionId(row.transaction_id),
      orderCode: `#${row.order?.order_code ?? "-"}`,
      customer: row.order?.customer?.name ?? t("common.guest"),
      amount: `RM ${Number(row.amount).toFixed(2)}`,
      method: PAYMENT_I18N_KEYS[row.method] ? payLabel(row.method) : row.method,
      type: row.type,
      typeLabel: typeLabelFor(row.type),
      status: row.status,
      statusLabel: statusLabelFor(row.status),
      reason: row.reason ? cancelReasonLabel(row.reason, i18n.language) : "-",
      date: `${formatOrderDate(row.created_at, i18n.language)} ${formatOrderTime(row.created_at, i18n.language)}`,
    }));
    const headers = [
      t("refunds.colRefundId"),
      t("refunds.colTxnId"),
      t("refunds.colCustomerOrder"),
      t("refunds.colAmount"),
      t("refunds.colType"),
      t("refunds.colStatus"),
      t("transactions.colDateTime"),
      t("refunds.colReason"),
    ];
    const rowsPerPage = 12;
    const pages = rows.length
      ? Array.from({ length: Math.ceil(rows.length / rowsPerPage) }, (_, index) => rows.slice(index * rowsPerPage, index * rowsPerPage + rowsPerPage))
      : [[]];

    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
      const pageRows = pages[pageIndex];
      const parts: string[] = [
        `<svg xmlns="http://www.w3.org/2000/svg" width="${pageWidth}" height="${pageHeight}" viewBox="0 0 ${pageWidth} ${pageHeight}">`,
        `<rect width="100%" height="100%" fill="#ffffff"/>`,
        `<style>text{font-family:Arial,'PingFang SC','Microsoft YaHei','Noto Sans CJK SC',sans-serif;dominant-baseline:alphabetic}</style>`,
        `<line x1="${margin}" y1="164" x2="${pageWidth - margin}" y2="164" stroke="#d1d5db" stroke-width="2"/>`,
        svgText([t("refunds.pageHeading")], margin + 24, 82, { size: 32, weight: 900, color: "#111827" }),
        svgText([`${t("refunds.dateRange")}: ${dateLabel}`], margin + 24, 120, { size: 17, weight: 700, color: "#475569" }),
        svgText([`${t("refunds.statUpdatedNow")} · ${pageIndex + 1}/${pages.length}`], margin + 24, 148, { size: 14, weight: 600, color: "#64748b" }),
      ];

      const summary = [
        { label: t("refunds.statTotalCount"), value: String(totalCount) },
        { label: t("refunds.statTotalAmount"), value: stats ? `RM ${Number(stats.totalAmount).toFixed(2)}` : "-" },
        { label: t("refunds.statTodayAmount"), value: stats ? `RM ${Number(stats.todayAmount).toFixed(2)}` : "-" },
      ];
      summary.forEach((card, index) => {
        const x = pageWidth - margin - 600 + index * 200;
        parts.push(svgText([card.label], x + 16, 105, { size: 12, weight: 800, color: "#64748b" }));
        parts.push(svgText([card.value], x + 16, 132, { size: 18, weight: 900, color: "#111827" }));
      });

      let y = 198;
      const headerHeight = 50;
      const rowHeight = 64;
      parts.push(`<rect x="${margin}" y="${y}" width="${tableWidth}" height="${headerHeight}" fill="#f3f4f6"/>`);
      let x = margin + 16;
      headers.forEach((header, index) => {
        parts.push(svgText(wrapPdfText(header, Math.floor((colWidths[index] - 12) / 13)).slice(0, 2), x + 10, y + 33, { size: 13, weight: 900, color: "#374151", lineHeight: 15 }));
        x += colWidths[index];
      });
      y += headerHeight + 34;

      if (!pageRows.length) {
        parts.push(svgText([t("common.noData", { defaultValue: "No data" })], margin + 32, y + 82, { size: 24, weight: 800, color: "#867461" }));
      }

      pageRows.forEach((row, index) => {
        const rowY = y + index * rowHeight;
        if (index % 2 === 0) {
          parts.push(`<rect x="${margin}" y="${rowY - 28}" width="${tableWidth}" height="${rowHeight}" fill="#f9fafb"/>`);
        }
        parts.push(`<line x1="${margin}" y1="${rowY + 36}" x2="${pageWidth - margin}" y2="${rowY + 36}" stroke="#e5e7eb" stroke-width="1"/>`);
        x = margin + 16;
        const orderCustomer = [row.customer, row.orderCode];
        const cells = [
          wrapPdfText(row.refundId, 10),
          wrapPdfText(row.txnId, 13),
          orderCustomer,
          wrapPdfText(`${row.amount}\n${row.method}`, 13),
          [row.typeLabel],
          [row.statusLabel],
          wrapPdfText(row.date, 14),
          wrapPdfText(row.reason, 34).slice(0, 2),
        ];
        cells.forEach((lines, cellIndex) => {
          parts.push(svgText(lines, x + 10, rowY, {
            size: cellIndex === 0 || cellIndex === 1 || cellIndex === 3 ? 15 : 14,
            weight: cellIndex === 0 || cellIndex === 1 || cellIndex === 3 ? 800 : 600,
            color: cellIndex === 3 ? "#111827" : "#334155",
            lineHeight: 18,
          }));
          x += colWidths[cellIndex];
        });
      });

      parts.push("</svg>");
      const image = await renderRefundPdfPageToPng(parts.join(""), pageWidth, pageHeight);
      if (pageIndex > 0) doc.addPage();
      doc.addImage(image, "PNG", 0, 0, pageWidthMm, pageHeightMm);
    }

    doc.save(`refund-report-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
  }

  const columns: ColumnsType<AdminRefundItem> = [
    {
      title: TH("refunds.colRefundId"),
      key: "rfd_id", width: 130,
      render: (_, row) => (
        <div>
          <div className="admin-data-code text-[13px] font-extrabold text-[#191c1d]">{refundDisplayId(row.id)}</div>
          <div className="admin-data-secondary admin-refunds-date text-[11px] text-[#867461]">
            {formatOrderDate(row.created_at, i18n.language)}<br />
            {formatOrderTime(row.created_at, i18n.language)}
          </div>
        </div>
      ),
    },
    {
      title: TH("refunds.colTxnId"),
      key: "txn_id", width: 160,
      render: (_, row) => (
        <div
          className="cursor-pointer"
          onClick={() => navigate(`/transactions/${row.transaction_id}?from=refunds`, { state: { activeNav: "/refunds" } })}
        >
          <div className="admin-data-code font-mono text-xs font-black text-[#191c1d]">
            {formatTransactionId(row.transaction_id)}
          </div>
        </div>
      ),
    },
    {
      title: TH("refunds.colCustomerOrder"),
      key: "customer", width: 160,
      render: (_, row) => (
        <div className="min-w-0">
          <div className="admin-data-customer truncate text-sm font-black text-[#191c1d]">{row.order?.customer?.name ?? t("common.guest")}</div>
          <div className="admin-data-secondary admin-data-date mt-1 text-xs font-bold text-[#7a6f65]">#{row.order?.order_code ?? "—"}</div>
        </div>
      ),
    },
    {
      title: TH("refunds.colAmount"),
      key: "amount", width: 110,
      render: (_, row) => (
        <div>
          <div className="admin-data-amount text-[15px] font-extrabold text-[#191c1d]">
            RM {Number(row.amount).toFixed(2)}
          </div>
          <div className="admin-data-secondary mt-0.5 text-[10px] text-[#867461]">
            {PAYMENT_I18N_KEYS[row.method] ? payLabel(row.method) : row.method}
          </div>
        </div>
      ),
    },
    {
      title: TH("refunds.colType"),
      key: "type", width: 100,
      render: (_, row) => <TypeBadge type={row.type} />,
    },
    {
      title: TH("refunds.colReason"),
      dataIndex: "reason", key: "reason", width: 140,
      render: (v: string) => <span className="admin-refunds-reason text-xs text-[#534434]">{v ? cancelReasonLabel(v, i18n.language) : v}</span>,
    },
    {
      title: TH("refunds.colStatus"),
      key: "status", width: 130,
      render: (_, row) => <StatusPill status={row.status} />,
    },
    {
      title: TH("refunds.colAction"),
      key: "actions", width: 56, fixed: "right",
      render: (_, row) => {
        const items: MenuProps["items"] = [
          ...(row.status === "pending" ? [{
            key: "update",
            label: <span className="text-xs font-bold tracking-[0.5px]">{t("refunds.menuUpdateStatus").toUpperCase()}</span>,
            onClick: () => setUpdateTarget(row),
          }] : []),
          {
            key: "detail",
            label: <span className="text-xs font-bold tracking-[0.5px]">{t("refunds.menuViewDetails").toUpperCase()}</span>,
            onClick: () => navigate(`/refunds/${row.id}`),
          },
          {
            key: "order",
            label: <span className="text-xs font-bold tracking-[0.5px]">{t("refunds.menuViewOrders").toUpperCase()}</span>,
            onClick: () => navigate(`/orders/${row.order?.id ?? ""}`, { state: { activeNav: "/refunds" } }),
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <button type="button" className="admin-data-more inline-flex cursor-pointer items-center justify-center border-none bg-transparent p-2.5 text-slate-500">
              <MoreOutlined className="text-lg" />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  // ── Stat cards config ──
  const statCards = [
    {
      label: t("refunds.statTotalAmount"),
      value: stats ? `RM ${Number(stats.totalAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}` : "—",
      sub: stats ? (stats.changePercent === null ? t("refunds.statSubNoLastMonth") : t("refunds.statSubVsLastMonth", { pct: `${stats.changePercent > 0 ? "+" : ""}${stats.changePercent}%` })) : "",
    },
    {
      label: t("refunds.statTotalCount"),
      value: stats ? String(stats.totalCount) : "—",
      sub: t("refunds.statAccumulatedMonth"),
    },
    {
      label: t("refunds.statPartialCount"),
      value: stats ? String(stats.partialCount) : "—",
      sub: stats && stats.totalCount > 0
        ? t("refunds.statPartialPct", { pct: Math.round(stats.partialCount / stats.totalCount * 100) })
        : "",
    },
    {
      label: t("refunds.statTodayAmount"),
      value: stats ? `RM ${Number(stats.todayAmount).toFixed(2)}` : "—",
      sub: t("refunds.statUpdatedNow"),
    },
  ];

  return (
    <AdminShell>
      <div className={`admin-refunds-page admin-txn-page ${REFUNDS_PAGE_CLASS}`}>
        <div className="mx-auto">
          <div ref={filterRef} className={`admin-refunds-filter admin-txn-filter admin-fixed-filter ${REFUNDS_FILTER_CLASS}`}>
            <div className="mx-auto">
              <div className="admin-txn-toolbar mb-5 flex flex-wrap items-end justify-between gap-4">
                <div className="admin-txn-heading flex min-w-[220px] flex-col items-start gap-[9px]">
                  <div className="admin-page-title m-0 text-2xl font-black leading-8 tracking-[-0.6px] text-[#191c1d]">{t("refunds.pageHeading")}</div>
                  <div className="admin-txn-period flex w-full items-center gap-2">
                    <span className="inline-block size-2 shrink-0 rounded-full bg-[#006c49]" />
                    <span className="text-xs font-medium leading-4 text-[#534434]">{t("refunds.statUpdatedNow")}</span>
                  </div>
                </div>

                <div className="admin-txn-tools">
                  <div className="admin-txn-search relative w-full max-w-[420px] min-w-[260px] sm:w-[360px] xl:w-[420px]">
                    <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#867461]" />
                    <input
                      type="text"
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder={t("refunds.placeholderSearch")}
                      className="admin-txn-search-input h-11 w-full rounded-lg border border-[#e7e1db] bg-white pl-11 pr-11 text-sm font-semibold text-[#191c1d] outline-none shadow-[0_12px_28px_rgba(25,28,29,0.06)] placeholder:text-[#867461] focus:border-[#ea580c] focus:ring-4 focus:ring-[#ea580c]/10"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        className="admin-txn-search-clear absolute right-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border-none bg-[#f3f4f5] text-[#534434] transition hover:bg-[#ffedd5] hover:text-[#ea580c]"
                        aria-label={t("common.clearFilters")}
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={exportRefundsPdf}
                    className="admin-button admin-txn-export admin-refunds-export inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-lg border px-4 transition"
                  >
                    <DownloadOutlined />
                    {t("refunds.exportPdf")}
                  </button>
                </div>
              </div>

              <div className="admin-txn-stats mb-5 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                {statCards.map((card, index) => (
                  <div key={card.label} className={`admin-kpi-card admin-txn-stat-card admin-refunds-stat-card flex flex-col ${index === 0 ? "admin-txn-stat-card-refund" : index === 3 ? "admin-kpi-card-revenue" : ""}`}>
                    <div className="admin-kpi-heading flex">
                      <div className="admin-kpi-label">{card.label}</div>
                      <div className="admin-kpi-icon" aria-hidden="true">
                        {index === 0 ? <ReloadOutlined /> : index === 1 ? <ContainerOutlined /> : index === 2 ? <PieChartOutlined /> : <CalendarOutlined />}
                      </div>
                    </div>
                    <div className="admin-kpi-value flex-1 tabular-nums">
                      {card.value.startsWith("RM ") ? (
                        <div className="admin-kpi-money">
                          <span className="admin-kpi-currency">RM</span>{" "}
                          <span className="admin-kpi-amount">{card.value.slice(3)}</span>
                        </div>
                      ) : card.value}
                    </div>
                    {card.sub && <div className="admin-refunds-stat-sub">{card.sub}</div>}
                  </div>
                ))}
              </div>

              <div className="admin-txn-filter-panel flex items-center gap-3 rounded-lg border border-[#eee8e2] bg-white/85 p-2 shadow-[0_12px_30px_rgba(25,28,29,0.05)] backdrop-blur max-lg:flex-col max-lg:items-stretch">
                <div className="admin-refunds-filter-fields admin-txn-filter-fields grid min-w-0 flex-1 grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
                  <div className="admin-refunds-date-control relative">
                    <RangePicker
                      value={dateRange ? [dayjs(dateRange[0]).format("YYYY-MM-DD"), dayjs(dateRange[1]).format("YYYY-MM-DD")] : undefined}
                      className={`admin-refunds-date-input h-10 w-full ${dateRange ? "pr-12" : ""}`}
                      onChange={(value, s) => {
                        const [from, to] = (s ?? value) as [string, string];
                        setDateRange(from && to ? [
                          dayjs(from).startOf("day").toISOString(),
                          dayjs(to).endOf("day").toISOString(),
                        ] : null);
                        setPage(1);
                      }}
                    />
                    {dateRange && (
                      <button
                        type="button"
                        onClick={() => {
                          setDateRange(null);
                          setPage(1);
                        }}
                        className="admin-refunds-date-clear absolute right-2.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border-none bg-[#f3f4f5] text-[#534434] transition hover:bg-[#ffedd5] hover:text-[#ea580c]"
                        aria-label={t("common.clearFilters")}
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                  <Select
                    value={type}
                    onChange={(v) => { setType(v as string | undefined); setPage(1); }}
                    allowClear
                    className="h-10 min-h-10 w-full"
                    placeholder={t("refunds.placeholderAllTypes")}
                    options={[
                      { value: "full", label: t("refunds.typeFull") },
                      { value: "partial", label: t("refunds.typePartial") },
                    ]}
                  />
                  <Select
                    value={method}
                    onChange={(v) => { setMethod(v as string | undefined); setPage(1); }}
                    allowClear
                    className="h-10 min-h-10 w-full"
                    placeholder={t("refunds.placeholderAllMethods")}
                    options={Object.keys(PAYMENT_I18N_KEYS).map((v) => ({ value: v, label: payLabel(v) }))}
                  />
                  <Select
                    value={status}
                    onChange={(v) => { setStatus(v as string | undefined); setPage(1); }}
                    allowClear
                    className="h-10 min-h-10 w-full"
                    placeholder={t("refunds.placeholderAllStatus")}
                    options={[
                      { value: "pending", label: t("refunds.statusPending") },
                      { value: "success", label: t("refunds.statusSuccess") },
                      { value: "rejected", label: t("refunds.statusRejectedPill") },
                      { value: "failed", label: t("refunds.statusFailed") },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Keep the measured fixed header clear; rows scroll with the page,
              matching Transactions without changing filters or pagination. */}
          <div style={{ height: filterHeight ? filterHeight + 24 : 320 }} />
          <div className="admin-txn-table-panel admin-data-table-panel">
            <Table<AdminRefundItem>
              columns={columns}
              dataSource={refunds}
              rowKey="id"
              loading={isLoading}
              rowClassName={rowClassName}
              size="middle"
              scroll={{ x: 1000 }}
              pagination={{ current: page, total: totalCount, pageSize: 10, showAlways: true, onChange: (p) => setPage(p) }}
            />
          </div>
        </div>
      </div>

      {updateTarget && (
        <UpdateStatusModal
          refund={updateTarget}
          onClose={() => setUpdateTarget(null)}
        />
      )}
    </AdminShell>
  );
}
